import { Stack, Typography, useTheme } from "@mui/material";
import type { LucideIcon } from "lucide-react";
import type { FC } from "react";
import type { ObjPathProxy } from "ts-object-path";
import type { MyPlannResourceDictionary } from "web_myplann/Infrastructure/Translations/ResourceDictionary";
import { useResource } from "web_myplann/Infrastructure/Translations/Resources";
import { InspirationSection } from "web_myplann/Inspiration/Components/InspirationSection";
import { IconBox } from "web_myplann/Shared/Components/IconBox";

export type InspirationTimelineStep = {
	icon: LucideIcon;
	step: ObjPathProxy<
		MyPlannResourceDictionary,
		{ title: string; duration: string; desc: string }
	>;
	values?: Record<string, string | number>;
};

type Props = {
	title: string;
	steps: InspirationTimelineStep[];
};

export const InspirationTimeline: FC<Props> = ({ title, steps }) => {
	const { t } = useResource();
	const theme = useTheme();

	return (
		<InspirationSection title={title}>
			{steps.map(({ icon: Icon, step, values }, index) => {
				const last = index === steps.length - 1;
				const duration = t(step.duration);
				return (
					<Stack key={t(step.title)} direction="row" gap={1.2}>
						<Stack alignItems="center">
							<IconBox bg={theme.palette.blue[100]} size={32}>
								<Icon
									size={theme.iconSize.sm}
									color={theme.palette.grey[900]}
								/>
							</IconBox>
							{!last && (
								<Stack
									flex={1}
									width="2px"
									my={0.4}
									bgcolor={theme.palette.blue[100]}
								/>
							)}
						</Stack>
						<Stack pb={last ? 0 : 1.6} gap={0.2}>
							<Stack
								direction="row"
								alignItems="baseline"
								gap={0.8}
								flexWrap="wrap"
							>
								<Typography variant="bodyMedium">{t(step.title)}</Typography>
								{duration && (
									<Typography variant="hint" color={theme.palette.grey[400]}>
										{duration}
									</Typography>
								)}
							</Stack>
							<Typography variant="hint" color={theme.palette.grey[500]}>
								{t(step.desc, values)}
							</Typography>
						</Stack>
					</Stack>
				);
			})}
		</InspirationSection>
	);
};
